type CSSPropertiesLike = Record<string, string | number | undefined>;

export class CSSDOMRenderer {
	// TODO: These should probably use binary stuff, there's no guarantee for collision now.
	private static BREAK_POINT_DIGIT = 1 as const;
	private static KEY_DIGIT = 100 as const;
	private static VALUE_DIGIT = 10000 as const;

	private styleElement: HTMLStyleElement | null = null;

	// Store for key value pairs on the CSS attributes. The value (in the key
	// value pair) being an increment to give each item a unique identifier.
	private readonly cachedKeys = new Map<string, number>();
	private key_i = 0;

	// Store for key value pairs on the CSS attribute's value. The value (in the
	// key value pair) being an increment to give each item a unique identifier.
	private readonly cachedValues = new Map<string, number>();
	private values_i = 0;

	// The goal of this module is to maintain idempotent results for each
	// successive insert request. All Hex ids being generated should be the
	// same given the same input. To avoid indefinitely adding the same rule,
	// this set exists as a look up and it's 100% trusting that no collisions may
	// occur.
	private readonly insertedRules = new Set<string>();
	private insertIndex = 0;

	// TODO: Type this better and use Cellula breakpoints and terminology.
	private breakpointMapping = {
		base: 0,
		S: 1,
		M: 2,
		L: 3,
	} as const;

	private cellulaId: string;

	constructor(cellulaId: string) {
		this.cellulaId = cellulaId;
	}

	/**
	 * React works out hooks bottom up. We want the StyleElement to be created before React does stuff.
	 * In a future we should avoid the dependency towards document stuff. This is not SSR friendly as is.
	 *
	 * @internal
	 */
	public QUIRK_HACK_TO_BE_USED_IN_RENDER_BODY(): void {
		this.create();
	}

	/**
	 * Creates a HTMLStyleElement to put all the style rules in.
	 *
	 * @internal
	 */
	public create(): void {
		if (this.styleElement != null) return;

		const styleElement = document.createElement('style');
		styleElement.appendChild(document.createTextNode(''));
		styleElement.type = 'text/css';
		styleElement.setAttribute('id', `layout-provider-${this.cellulaId}`);
		document.head.appendChild(styleElement);

		this.styleElement = styleElement;
	}

	/* @internal */
	public remove(): void {
		if (this.styleElement == null) return;

		document.head.removeChild(this.styleElement);
		this.styleElement = null;
	}

	public convertStyleIntoClassName(breakpoints: {
		base: CSSPropertiesLike;
		S?: CSSPropertiesLike;
		M?: CSSPropertiesLike;
		L?: CSSPropertiesLike;
	}): string[] {
		const classNames: string[] = [];

		// Goes through all the breakpoints to generate unique insertions for each wanted breakpoint.
		const entries = Object.entries(breakpoints);
		for (const [key, value] of entries) {
			if (value == null) continue; // No point of handling null/undefined values, will just leave empty CSS declarations. Go next.

			const breakpoint = this.breakpointMapping[key as keyof CSSDOMRenderer['breakpointMapping']];

			// Goes through each CSS attribute and it's value to generate unique
			// insertions for that particular combo.
			const entries = Object.entries(value);
			for (const [key, value] of entries) {
				if (value == null) continue; // No point of handling null/undefined values, will just leave empty CSS declarations. Go next.

				try {
					const convertedKey = this.toHyphenCase(key);
					const convertedValue = this.numberToString(value);

					let cachedKey = this.cachedKeys.get(convertedKey);
					let cachedValue = this.cachedValues.get(convertedValue);

					// If either the CSS attribute or its corresponding value has not been
					// recorded, each needs to get a unique signature so it may be
					// accessed and read later.
					if (cachedKey == null) {
						cachedKey = ++this.key_i * CSSDOMRenderer.KEY_DIGIT;
						this.cachedKeys.set(convertedKey, cachedKey);
					}
					if (cachedValue == null) {
						cachedValue = ++this.values_i * CSSDOMRenderer.VALUE_DIGIT;
						this.cachedValues.set(convertedValue, cachedValue);
					}

					const hexId = this.getHexId(breakpoint, cachedKey, cachedValue);

					this.insertRule(hexId, breakpoint, convertedKey, convertedValue);

					classNames.push(hexId);
				} catch (error: unknown) {
					console.error(`Could not insert rule "${key}: ${value}"`, error);
				}
			}
		}

		return classNames;
	}

	private getStyleElement(): HTMLStyleElement {
		if (this.styleElement == null) {
			throw new Error('"styleElement" has not been set, was this method run outside of the provider?');
		}

		return this.styleElement;
	}

	private getSheet(): CSSStyleSheet {
		const element = this.getStyleElement();
		if (element.sheet == null) {
			throw new Error('"sheet" is null, is this method being run outside a browser environment');
		}

		return element.sheet;
	}

	private getHexId(breakpoint: number, key: number, value: number): string {
		const x = key * CSSDOMRenderer.KEY_DIGIT;
		const y = value * CSSDOMRenderer.VALUE_DIGIT;
		const z = breakpoint * CSSDOMRenderer.BREAK_POINT_DIGIT;
		const index = x + y + z;
		// CSS identifier may only contain the characters "[a-zA-Z0-9] and ISO 10646
		// characters U+00A0 and higher, plus the hyphen (-) and the underscore (_)".
		// Parsing to Hex will sometimes produce a starting character that is a
		// number, and then the rule will fail. Hence the prefixed underscore.
		// See: https://www.w3.org/TR/CSS2/syndata.html#characters
		const hexId = '_' + this.cellulaId + '_' + index.toString(16);

		return hexId;
	}

	/**
	 * Applies a rule into the CSSStyleSheet based on the properties.
	 */
	private insertRule(hexId: string, breakpoint: number, cssProperty: string, cssValue: string): void {
		if (this.insertedRules.has(hexId)) return;

		let rule = '';
		if (breakpoint === 0) {
			rule = `.${hexId} { ${cssProperty}: ${cssValue}; }`;
		} else {
			let mappedBreakpoint: number;
			switch (breakpoint) {
				case 1: // Small
					mappedBreakpoint = 600;
					break;
				case 2: // Medium
					mappedBreakpoint = 905;
					break;
				case 3: // Large
					mappedBreakpoint = 1440;
					break;
				default:
					mappedBreakpoint = 0;
			}

			rule = `@media screen and (min-width: ${mappedBreakpoint}px) { .${hexId} { ${cssProperty}: ${cssValue}; } }`;
		}

		const sheet = this.getSheet();
		sheet.insertRule(rule, this.insertIndex++);
		this.insertedRules.add(hexId);
	}

	/**
	 *  Replace any upper-case letter with a dash and the lower-case variant.
	 */
	private toHyphenCase(value: string): string {
		return value.includes('-') ? value : value.replace(/[A-Z]/g, (capital) => '-' + capital.toLowerCase());
	}

	private numberToString(value: number | string): string {
		return typeof value === 'number' ? value.toString() : value;
	}
}
